import React from "react";
import type { UserDetail } from "../types/users.types";

/** One way of reaching them, labelled. */
const Contact: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex items-baseline gap-2 min-w-0">
    <span className="text-[14px] text-ink-muted flex-shrink-0">{label}</span>
    <span className="text-[14px] font-medium text-ink truncate">{value}</span>
  </div>
);

interface UserContactCardProps {
  user: UserDetail;
}

/**
 * How to reach this account, and where they are.
 *
 * Nothing here is a link. A `tel:` or a `mailto:` in an admin console is an
 * accident waiting to happen — the reader is here to look somebody up, not to
 * ring them, and a mis-click that opens a mail client addressed to a customer
 * is a worse outcome than having to copy an address by hand.
 */
const UserContactCard: React.FC<UserContactCardProps> = ({ user }) => (
  <section className="rounded-md border border-line bg-white">
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 lg:gap-4 p-4 sm:p-6">
      {/* Printed as it was given. A "+91" was written in front of this once,
          from back when the number arrived bare; the account endpoint sends it
          in full, so prefixing would have read "+91 +919876543210". */}
      <Contact label="Phone" value={user.phone} />
      <Contact label="WhatsApp" value={user.whatsapp} />
      <Contact label="Email" value={user.email} />
    </div>

    {/* On its own rule below: an address is a paragraph, not a field, and
        squeezing it into the row above would truncate the one thing on this
        card that is worth reading in full. */}
    <div className="border-t border-line px-4 sm:px-6 py-4">
      <p className="text-[14px] text-ink-muted">Address</p>
      <p className="mt-1 text-[14px] font-medium text-ink leading-[1.6]">
        {user.address}
      </p>
    </div>
  </section>
);

export default UserContactCard;
